import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import apiClient from '../../api/apiClient';

// تبدیل اعداد انگلیسی به فارسی
const toPersianNumbers = (str) => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
};

const PurchaseModal = ({ product, isOpen, onClose, onSuccess }) => {
  const [discountCode, setDiscountCode] = useState('');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [isBuying, setIsBuying] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setDiscountCode('');
        setGiftCardCode('');
        setMessage('');
        setIsBuying(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // اگر کاربر قبلاً دسترسی دارد، مودال را ببند و دسترسی را به‌روز کن
  if (product?.has_access) {
    if (onSuccess) onSuccess();
    onClose();
    return null;
  }

  const handleBuy = async () => {
    if (!product || isBuying) return;

    setIsBuying(true);
    setMessage('');

    try {
      // فعال‌سازی کارت هدیه
      if (giftCardCode.trim()) {
        try {
          await apiClient.post('/wallet/gift-cards/redeem/', { code: giftCardCode.trim() });
          setMessage('✅ کارت هدیه با موفقیت فعال شد.');
        } catch (err) {
          const errMsg = err.response?.data?.error || err.response?.data?.detail || 'کد کارت هدیه نامعتبر است.';
          setMessage(`❌ ${errMsg}`);
          setIsBuying(false);
          return;
        }
      }

      // خرید محصول
      const payload = { product_slug: product.slug };
      if (discountCode.trim()) payload.discount_code = discountCode.trim();

      const response = await apiClient.post('/purchases/orders/create/', payload);

      if (response.data.success) {
        setMessage('✅ خرید با موفقیت انجام شد.');
        if (onSuccess) onSuccess();
        setTimeout(() => onClose(), 1500);
      } else {
        throw new Error(response.data.detail || 'خطا در ثبت سفارش.');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      let errMsg = err.response?.data?.error || err.response?.data?.detail || 'خطا در خرید.';
      const status = err.response?.status;

      // تشخیص خرید تکراری (400 با پیام خاص)
      const isDuplicate = status === 400 && (
        errMsg.includes('قبلاً') ||
        errMsg.includes('تکراری') ||
        errMsg.includes('already purchased') ||
        errMsg.includes('قبلا خریداری')
      );

      if (isDuplicate) {
        setMessage('⚠️ شما قبلاً این محصول را خریداری کرده‌اید. صفحه در حال به‌روزرسانی...');
        if (onSuccess) onSuccess();  // باعث به‌روزرسانی has_access در صفحه می‌شود
        setTimeout(() => onClose(), 1500);
        return; // از تابع خارج می‌شویم تا به finally نرود و isBuying را تغییر ندهد
      } else if (errMsg.includes('موجودی') || errMsg.includes('کافی نیست')) {
        setMessage('⚠️ موجودی کیف پول کافی نیست. لطفاً کیف پول را شارژ کنید.');
      } else {
        setMessage(`❌ ${errMsg}`);
      }
      setIsBuying(false);
    } finally {
      // اگر isDuplicate نبود و در catch ادامه یافت، اینجا isBuying false می‌شود
    }
  };

  const getDisplayPrice = () => {
    if (product.price_display) return toPersianNumbers(product.price_display);
    if (product.price !== undefined && product.price !== null) {
      const formatted = `${Number(product.price).toLocaleString()} تومان`;
      return toPersianNumbers(formatted);
    }
    return 'رایگان';
  };

  const displayPrice = getDisplayPrice();

  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-2xl shadow-2xl p-6 rtl animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[12px] font-bold text-gray-800">تکمیل خرید</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <img src={product.cover_url || '/placeholder.jpg'} alt={product.title} className="w-14 h-14 object-cover rounded-lg" />
          <div>
            <p className="font-medium text-gray-800 text-[11px]">{product.title}</p>
            <p className="text-xs text-gray-500">{displayPrice}</p>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-xs text-gray-600 block mb-1">کد تخفیف (اختیاری)</label>
          <input type="text" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} placeholder="کد تخفیف" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[11px]" />
        </div>

        <div className="mb-4">
          <label className="text-xs text-gray-600 block mb-1">کارت هدیه (اختیاری)</label>
          <input type="text" value={giftCardCode} onChange={(e) => setGiftCardCode(e.target.value)} placeholder="کد کارت هدیه" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[11px]" />
        </div>

        {message && (
          <div className={`text-[11px] rounded-lg p-2 mb-3 text-center ${
            message.includes('✅') ? 'bg-green-100 text-green-800' :
            message.includes('⚠️') ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
          }`}>
            {message}
            {message.includes('شارژ') && (
              <Link to="/profile/wallet" className="block mt-1 text-purple-700 underline text-xs" onClick={onClose}>
                شارژ کیف پول
              </Link>
            )}
          </div>
        )}

        <button onClick={handleBuy} disabled={isBuying} className="w-full bg-purple-700 text-white py-3 rounded-xl font-bold text-[11px] hover:bg-purple-800 transition flex items-center justify-center gap-2 disabled:opacity-70">
          {isBuying && <Loader2 size={18} className="animate-spin" />}
          {isBuying ? 'در حال پرداخت...' : `پرداخت ${displayPrice}`}
        </button>
      </div>
    </>,
    document.body
  );
};

export default PurchaseModal;